import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button, message, Cascader, Spin } from "antd";
import {
  CloseOutlined,
  PercentageOutlined,
  MailOutlined,
  PrinterOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/api";
import { createQuotation, updateQuotation } from "../../../services/quotationService";
import { getCompanySettings } from "../../../services/organizationService";
import { getDeals } from "../../../services/pipelineService";
import { buildProductCascaderOptions, cascaderSearchFilter, resolveCascaderProduct } from "../../../utils/productCatalogUtils";
import { generatePdfFromElement } from "../../../utils/pdfExportUtils";
import useAuthSession from "../../../hooks/useAuthSession";
import MailComposeDrawer from "../mail/MailComposeDrawer";
import "./quotation.css";

const resolveLogoUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:")) {
    return trimmed;
  }
  if (trimmed.startsWith("/")) {
    return trimmed;
  }
  return `${API_BASE_URL}/${trimmed}`;
};

const getDraftQuotationNumber = (issuedDate) => {
  const date = issuedDate ? new Date(issuedDate) : new Date();
  const yy = String(date.getFullYear()).slice(-2);
  return `QT${yy}Draft`;
};

export function parseDbTerms(rawTerms) {
  if (!rawTerms) return [];
  if (Array.isArray(rawTerms)) return rawTerms;
  if (typeof rawTerms === "string") {
    return rawTerms
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
  }
  return [];
}

const DEFAULT_DB_SETTINGS = {
  products: [],
  terms: [],
  bannerText: "Think Software,\nThink Softrate.",
  bannerTextColor: "#ffffff",
  bannerBgColor: "#785b18",
  bannerBgImage: "",
  quotationNote: "We aim to provide the best software to automate your business (Our charges are 50% less than market price with a mantra “High quality at affordable cost”).\nThe price mentioned above is exclusive of applicable tax.",
  bankDetails: null,
  bankDetails2: null,
  contactDetails: null,
  gstPercentage: 18,
  companyLogo: "",
  companyName: "",
  heroBanner: "",
  footerBanner: "",
  invoiceFooter: "",
};

export function parseCompanySettings(settings) {
  if (!settings) return DEFAULT_DB_SETTINGS;
  return {
    products: Array.isArray(settings.products) ? settings.products : [],
    terms: parseDbTerms(settings.invoiceTerms),
    bannerText: settings.quotationBannerText || "Think Software,\nThink Softrate.",
    bannerTextColor: settings.quotationBannerTextColor || "#ffffff",
    bannerBgColor: settings.quotationBannerBgColor || "#785b18",
    bannerBgImage: settings.quotationBannerBgImage || "",
    quotationNote: settings.quotationNote || "We aim to provide the best software to automate your business (Our charges are 50% less than market price with a mantra “High quality at affordable cost”).\nThe price mentioned above is exclusive of applicable tax.",
    bankDetails: settings.bankDetails || null,
    bankDetails2: settings.bankDetails2 || null,
    contactDetails: settings.contactDetails || null,
    gstPercentage: settings.gstPercentage !== undefined ? settings.gstPercentage : 18,
    companyLogo: settings.invoiceLogo || "",
    companyName: settings.companyName || "",
    heroBanner: settings.quotationHeroBanner || "",
    footerBanner: settings.quotationFooterBanner || "",
    invoiceFooter: settings.invoiceFooter || "",
  };
}

export function matchLeadProduct(lead, products) {
  const targetDealName = (lead?.dealName || lead?.productName || lead?.service || "").trim().toLowerCase();
  if (!targetDealName || !Array.isArray(products) || products.length === 0) {
    return null;
  }
  for (const p of products) {
    const pName = (p.name || "").trim().toLowerCase();
    if (pName === targetDealName || targetDealName.includes(pName) || pName.includes(targetDealName)) {
      return p;
    }
    if (Array.isArray(p.subProducts)) {
      for (const sp of p.subProducts) {
        const spName = (sp.name || "").trim().toLowerCase();
        const combined = `${pName} - ${spName}`.toLowerCase();
        if (spName === targetDealName || combined === targetDealName || targetDealName.includes(spName)) {
          return {
            ...sp,
            name: `${p.name} - ${sp.name}`,
            price: sp.price || sp.minPrice || p.minPrice || 0,
            description: sp.description || "",
          };
        }
      }
    }
  }
  return null;
}

export function getInitialQuotationFormState({ activeRecord, activeLead, activeCompanyCode, visibleProducts }) {
  if (activeRecord) {
    return {
      quoteNumberStr: activeRecord.quotationNumber || getDraftQuotationNumber(activeRecord.quotationDate),
      invoiceIssuedAt: activeRecord.quotationDate ? dayjs(activeRecord.quotationDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
      invoiceItems: (activeRecord.items || []).map((item) => ({
        name: item.name,
        quantity: Number(item.quantity) || 1,
        price: Number(item.rate ?? item.price ?? 0),
        description: item.description || "",
        sacHsn: item.sacHsn || "",
      })),
      quotationKindNoteDraft: activeRecord.kindNote || "",
      documentCompanyCode: activeRecord.companyCode || activeCompanyCode,
      selectedInvoiceProduct: null,
      invoicePrice: 0,
      invoiceQuantity: 1,
      documentGstPercentageOverride: activeRecord.gstPercentage !== undefined ? activeRecord.gstPercentage : null,
    };
  }

  const todayStr = dayjs().format("YYYY-MM-DD");
  const matchedProduct = matchLeadProduct(activeLead, visibleProducts);
  const price = matchedProduct ? Number(activeLead?.amount) || matchedProduct.price || matchedProduct.minPrice || 0 : 0;
  const initialItems = matchedProduct
    ? [
        {
          name: matchedProduct.name || "",
          quantity: 1,
          price,
          description: matchedProduct.description || "",
        },
      ]
    : [];

  return {
    quoteNumberStr: getDraftQuotationNumber(todayStr),
    invoiceIssuedAt: todayStr,
    invoiceItems: initialItems,
    quotationKindNoteDraft: "",
    documentCompanyCode: activeCompanyCode,
    selectedInvoiceProduct: matchedProduct,
    invoicePrice: price,
    invoiceQuantity: 1,
    documentGstPercentageOverride: null,
  };
}

export function formatBankDetails(dbBankDetails, dbBankDetails2, companyInfoBankDetails, hasCustomerGst) {
  let details = dbBankDetails || companyInfoBankDetails;
  if (!hasCustomerGst && dbBankDetails2?.bankName && dbBankDetails2?.accountNumber) {
    details = dbBankDetails2;
  }
  return [
    { label: "Bank", value: details?.bankName || "" },
    { label: "Acc", value: details?.accountNumber || "" },
    { label: "IFSC", value: details?.ifscCode || "" },
    { label: "Branch", value: details?.branchName || "" },
  ].filter((row) => row.value);
}

export async function generateQuotationEmailData({ activeLead, docNumber, invoiceIssuedAt, grandTotal, totalAmount, activeEmployeeName, companyDisplayName }) {
  const quoteAmount = totalAmount ?? grandTotal ?? 0;
  const customerCompanyName = activeLead?.leadCompanyName || activeLead?.companyName || activeLead?.contactName || "Customer";
  const cleanCustomerName = customerCompanyName
    .replace(/[^a-zA-Z0-9]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  const fileName = `${cleanCustomerName}_${docNumber}.pdf`;

  const previewEl = document.querySelector(".quotation-builder-preview");
  let quoteFile = null;
  if (previewEl) {
    try {
      message.loading({ content: "Preparing PDF attachment...", key: "quote-pdf", duration: 2 });
      quoteFile = await generatePdfFromElement(previewEl, fileName);
      message.destroy("quote-pdf");
    } catch (err) {
      console.error("Quotation PDF generation failed:", err);
    }
  }

  const emailSubject = `Quotation ${docNumber} - ${customerCompanyName}`;
  const emailBody = `<p>Dear ${activeLead?.contactName || "Sir/Madam"},</p>
<p>Thank you for your interest. Please find attached our quotation <strong>${docNumber}</strong> for <strong>${customerCompanyName}</strong>.</p>
<p><strong>Quotation Summary:</strong></p>
<ul>
  <li><strong>Quote Number:</strong> ${docNumber}</li>
  <li><strong>Date:</strong> ${invoiceIssuedAt}</li>
  <li><strong>Total:</strong> ₹${Math.round(quoteAmount).toLocaleString("en-IN")} (+ 18% GST will apply)</li>
</ul>
<p>Kindly review the attached quotation PDF file. Please let us know if you have any questions.</p>
<br/>
<p>Warm regards,<br/><strong>${activeEmployeeName || "Sales Team"}</strong><br/>${companyDisplayName}</p>`;

  return {
    to: activeLead?.directorEmailAddress || activeLead?.email || "",
    subject: emailSubject,
    html: emailBody,
    attachments: quoteFile ? [quoteFile] : [],
  };
}

function QuotationHeader({ isViewingSaved, quoteNumberStr, allowedCompanies, documentCompanyCode, setDocumentCompanyCode, activeCompanyCode, invoiceIssuedAt, handleDateChange, onClose }) {
  return (
    <div className="quotation-modal-header">
      <div>
        <div className="quotation-section-kicker">{isViewingSaved ? "Saved Quotation" : "Quotation Builder"}</div>
        <div style={{ margin: 0, fontSize: "1.25rem", color: "#0f172a", fontWeight: 700 }}>Quote {quoteNumberStr}</div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        {!isViewingSaved && allowedCompanies.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label htmlFor="quotation-header-company-select" style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
              Company
            </label>
            <select id="quotation-header-company-select" value={documentCompanyCode} onChange={(e) => setDocumentCompanyCode(e.target.value)} className="quotation-field-select" style={{ minWidth: 120, height: 32, padding: "2px 8px" }}>
              <option value={activeCompanyCode}>{activeCompanyCode} (Main)</option>
              {allowedCompanies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label htmlFor="quotation-header-quote-date" style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
            Quote Date
          </label>
          <input id="quotation-header-quote-date" type="date" value={invoiceIssuedAt} onChange={(e) => handleDateChange(e.target.value)} className="quotation-field-input" style={{ width: "auto", height: 32 }} />
        </div>

        <Button type="text" icon={<CloseOutlined />} onClick={onClose} style={{ color: "#64748b" }} />
      </div>
    </div>
  );
}

const formatDealStage = (stage) => {
  if (!stage) return "";
  return String(stage)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

function QuotationCompanyDealsSelector({ companyDeals, loadingDeals, invoiceItems, onAddDealItem, onAddAllDeals, companyName }) {
  if (loadingDeals) {
    return (
      <div className="quotation-control-card quotation-deals-card" style={{ padding: "10px 14px", textAlign: "center" }}>
        <Spin size="small" />
        <span style={{ fontSize: "0.75rem", color: "#64748b", marginLeft: 8 }}>
          Checking pipeline deals for {companyName || "client"}...
        </span>
      </div>
    );
  }

  if (!Array.isArray(companyDeals) || companyDeals.length === 0) {
    return null;
  }

  const isDealAdded = (deal) => {
    const dName = String(deal.dealName || "").trim().toLowerCase();
    return invoiceItems.some((item) => String(item.name || "").trim().toLowerCase() === dName);
  };

  const unaddedCount = companyDeals.filter((d) => !isDealAdded(d)).length;

  return (
    <div className="quotation-control-card quotation-deals-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, paddingBottom: 6, borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Pipeline Deals
          </span>
          <span style={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 500 }}>
            ({companyDeals.length})
          </span>
        </div>
        {unaddedCount > 0 && (
          <Button
            type="link"
            size="small"
            onClick={onAddAllDeals}
            style={{
              fontSize: "0.72rem",
              height: "auto",
              padding: 0,
              fontWeight: 500,
              color: "#2563eb",
            }}
          >
            Add all ({unaddedCount})
          </Button>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 180, overflowY: "auto" }}>
        {companyDeals.map((deal) => {
          const added = isDealAdded(deal);
          const dealKey = deal._id || deal.dealName;
          const stage = formatDealStage(deal.pipelineStage);
          const formattedAmount = (Number(deal.amount) || 0).toLocaleString("en-IN");

          return (
            <div
              key={dealKey}
              className="quotation-deal-item"
              style={{
                opacity: added ? 0.65 : 1,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1, marginRight: 8 }}>
                <span
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: added ? "#64748b" : "#1e293b",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={deal.dealName}
                >
                  {deal.dealName}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.7rem", color: "#64748b" }}>
                  <span style={{ fontWeight: 600, color: added ? "#64748b" : "#0f172a" }}>
                    ₹{formattedAmount}
                  </span>
                  {stage && (
                    <>
                      <span style={{ color: "#cbd5e1", fontSize: "0.65rem" }}>•</span>
                      <span style={{ color: "#64748b" }}>{stage}</span>
                    </>
                  )}
                </div>
              </div>

              {added ? (
                <span
                  style={{
                    fontSize: "0.72rem",
                    color: "#16a34a",
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <CheckOutlined style={{ fontSize: "0.68rem" }} /> Added
                </span>
              ) : (
                <Button
                  size="small"
                  onClick={() => onAddDealItem(deal)}
                  style={{
                    borderRadius: 5,
                    height: 24,
                    fontSize: "0.72rem",
                    padding: "0 8px",
                    borderColor: "#e2e8f0",
                    color: "#2563eb",
                    fontWeight: 500,
                    background: "#ffffff",
                  }}
                >
                  + Add
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuotationProductControls({ visibleProducts, selectedInvoiceProduct, handleProductSelect, invoicePrice, setInvoicePrice, invoiceQuantity, setInvoiceQuantity, handleAddInvoiceItem, editingItemIndex, handleCancelEditInvoiceItem }) {
  const cascaderOptions = useMemo(() => buildProductCascaderOptions(visibleProducts), [visibleProducts]);

  return (
    <div className="quotation-control-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#0f172a", textTransform: "uppercase" }}>
          {editingItemIndex !== null ? `Edit Quotation Item (#${editingItemIndex + 1})` : "Add Line Item"}
        </span>
        {editingItemIndex !== null && (
          <Button size="small" type="link" onClick={handleCancelEditInvoiceItem} style={{ fontSize: "0.75rem", padding: 0, height: "auto", color: "#64748b" }}>
            Cancel Edit
          </Button>
        )}
      </div>
      <div className="quotation-form-grid">
        <label className="quotation-mini-field">
          <span>Product / Sub-Product</span>
          <Cascader placeholder="Choose product / sub-product..." options={cascaderOptions} onChange={handleProductSelect} changeOnSelect showSearch={{ filter: cascaderSearchFilter }} allowClear style={{ width: "100%" }} />
        </label>
        <label className="quotation-mini-field">
          <span>Price (₹)</span>
          <input type="number" min={0} step="any" value={invoicePrice || ""} onChange={(e) => setInvoicePrice(e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)))} className="quotation-field-input" placeholder="0" />
        </label>
        <label className="quotation-mini-field">
          <span>Qty</span>
          <input type="number" value={invoiceQuantity} onChange={(e) => setInvoiceQuantity(Number(e.target.value))} className="quotation-field-input" min="1" />
        </label>
        {editingItemIndex !== null ? (
          <div style={{ display: "flex", gap: 6 }}>
            <Button type="primary" icon={<CheckOutlined />} onClick={handleAddInvoiceItem} disabled={!selectedInvoiceProduct} style={{ borderRadius: 6, height: 36, whiteSpace: "nowrap", padding: "0 12px", background: "#2563eb", borderColor: "#2563eb" }}>
              Update
            </Button>
            <Button onClick={handleCancelEditInvoiceItem} style={{ borderRadius: 6, height: 36, padding: "0 8px" }}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button type="primary" onClick={handleAddInvoiceItem} disabled={!selectedInvoiceProduct} style={{ borderRadius: 6, height: 36, whiteSpace: "nowrap", padding: "0 14px" }}>
            Add Item
          </Button>
        )}
      </div>
    </div>
  );
}

function QuotationItemsTable({ invoiceItems, handleEditInvoiceItem, handleRemoveInvoiceItem, editingItemIndex }) {
  return (
    <div
      className="quotation-control-card"
      style={{
        padding: 0,
        overflowX: "auto",
        overflowY: "hidden",
        flexShrink: 0,
        minHeight: "fit-content",
      }}
    >
      <table style={{ width: "100%", minWidth: 440, borderCollapse: "collapse", textAlign: "left", fontSize: "0.84rem" }}>
        <thead>
          <tr style={{ background: "#f8fafc", color: "#475569", borderBottom: "1px solid #e2e8f0" }}>
            <th style={{ padding: "10px 14px", fontWeight: 600, fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>Description</th>
            <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600, fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.03em", width: 50 }}>Qty</th>
            <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.03em", width: 90 }}>Price</th>
            <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.03em", width: 95 }}>Sum</th>
            <th style={{ padding: "10px 12px", width: 68, textAlign: "center", fontWeight: 600, fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {invoiceItems.map((item, i) => (
            <tr
              key={item.id || item.name || `item-${i}`}
              style={{
                borderBottom: "1px solid #f1f5f9",
                background: editingItemIndex === i ? "#eff6ff" : "transparent",
                transition: "background 0.15s ease",
              }}
            >
              <td style={{ padding: "13px 14px", color: "#0f172a" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "#0f172a" }}>{item.name}</span>
                    {editingItemIndex === i && (
                      <span style={{ fontSize: "0.65rem", background: "#3b82f6", color: "#fff", padding: "1px 5px", borderRadius: 3, fontWeight: 500 }}>Editing</span>
                    )}
                  </div>
                  {item.description && (
                    <span style={{ fontSize: "0.72rem", color: "#64748b", lineHeight: 1.3 }}>{item.description}</span>
                  )}
                </div>
              </td>
              <td style={{ padding: "13px 8px", textAlign: "center", color: "#334155", fontSize: "0.84rem" }}>{item.quantity}</td>
              <td style={{ padding: "13px 12px", textAlign: "right", color: "#334155", fontSize: "0.84rem" }}>₹{(Number(item.price) || 0).toLocaleString("en-IN")}</td>
              <td style={{ padding: "13px 12px", textAlign: "right", fontWeight: 700, color: "#0f172a", fontSize: "0.85rem" }}>₹{((Number(item.price) || 0) * (Number(item.quantity) || 1)).toLocaleString("en-IN")}</td>
              <td style={{ padding: "13px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                <Button type="text" size="small" icon={<EditOutlined style={{ color: "#2563eb", fontSize: "0.85rem" }} />} onClick={() => handleEditInvoiceItem(i)} title="Edit item" style={{ marginRight: 2, padding: "2px 4px" }} />
                <Button type="text" size="small" icon={<DeleteOutlined style={{ color: "#ef4444", fontSize: "0.85rem" }} />} onClick={() => handleRemoveInvoiceItem(i)} title="Remove item" style={{ padding: "2px 4px" }} />
              </td>
            </tr>
          ))}
          {invoiceItems.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: "28px 14px", textAlign: "center", color: "#94a3b8", fontSize: "0.82rem" }}>
                No quotation items added yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function QuotationGstRateSelector({ effectiveGstRate, hasCustomerGst, dbGstPercentage, documentGstPercentageOverride, setDocumentGstPercentageOverride }) {
  const standardRate = Number(dbGstPercentage) || 18;
  return (
    <div className="quotation-gst-selector">
      <div className="quotation-gst-selector-label">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <PercentageOutlined style={{ color: "#2563eb", fontSize: "0.85rem" }} />
          <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>
            GST Applicable Rate
          </span>
        </div>
        <span style={{ fontSize: "0.72rem", color: "#64748b", paddingLeft: 18, whiteSpace: "nowrap" }}>
          {effectiveGstRate}% {hasCustomerGst ? "• Client GSTIN Verified" : "• No Client GSTIN"}
        </span>
      </div>
      <div className="quotation-gst-selector-options">
        <button
          type="button"
          className={`quotation-gst-opt ${effectiveGstRate === standardRate && documentGstPercentageOverride !== -1 ? "active" : ""}`}
          onClick={() => setDocumentGstPercentageOverride(standardRate)}
          title={`Standard GST (${standardRate}%)`}
        >
          <span className="quotation-gst-opt-pct">{standardRate}%</span>
          <span className="quotation-gst-opt-label">Standard</span>
        </button>
        <button
          type="button"
          className={`quotation-gst-opt ${effectiveGstRate === 0 && documentGstPercentageOverride !== -1 ? "active" : ""}`}
          onClick={() => setDocumentGstPercentageOverride(0)}
          title="Zero Rated (0%)"
        >
          <span className="quotation-gst-opt-pct">0%</span>
          <span className="quotation-gst-opt-label">Zero Rated</span>
        </button>
        <button
          type="button"
          className={`quotation-gst-opt ${documentGstPercentageOverride === -1 ? "active" : ""}`}
          onClick={() => setDocumentGstPercentageOverride(-1)}
          title="GST Exempt (No Tax)"
        >
          <span className="quotation-gst-opt-pct">Exempt</span>
          <span className="quotation-gst-opt-label">No Tax</span>
        </button>
      </div>
    </div>
  );
}

function QuotationPreviewPage1({
  resolvedHeroBannerUrl,
  dbCompanyLogo,
  logoLoadError,
  setLogoLoadError,
  companyDisplayName,
  quoteNumberStr,
  invoiceIssuedAt,
  activeLead,
  invoiceItems,
  effectiveGstRate,
  documentGstPercentageOverride,
  hasCustomerGst,
  kindNote,
}) {
  const logoUrl = resolveLogoUrl(dbCompanyLogo);

  const getGstFootnoteText = () => {
    if (documentGstPercentageOverride === -1) {
      return "* GST Exempt";
    }
    if (effectiveGstRate === 0 && documentGstPercentageOverride !== -1) {
      return `* 0% GST (${hasCustomerGst ? "Zero-Rated" : "No GSTIN"})`;
    }
    return `* ${effectiveGstRate || 18}% GST will apply`;
  };

  return (
    <section className="quotation-page">
      <header
        className="quotation-hero"
        style={
          resolvedHeroBannerUrl
            ? {
                backgroundImage: `linear-gradient(to right, rgba(15, 23, 42, 0.25) 0%, rgba(15, 23, 42, 0.45) 50%, rgba(15, 23, 42, 0.72) 100%), url("${resolvedHeroBannerUrl}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : {}
        }
      >
        <div className="quotation-hero-logo">{logoUrl && !logoLoadError ? <img src={logoUrl} alt={companyDisplayName} onError={() => setLogoLoadError(true)} style={{ maxHeight: 58, maxWidth: 220, objectFit: "contain", display: "block" }} /> : <div className="quotation-hero-text-logo">{companyDisplayName}</div>}</div>
        <div className="quotation-hero-title">
          <div className="quotation-hero-main-title">Quotation</div>
          <span className="quotation-hero-label">Quote Number</span>
          <strong className="quotation-hero-number">{quoteNumberStr}</strong>
          <span className="quotation-hero-date">{dayjs(invoiceIssuedAt).format("DD MMM YYYY")}</span>
        </div>
      </header>

      <section className="quotation-letter">
        <div className="quotation-salutation">Dear {activeLead?.contactName || "Sir/Ma'am"},</div>
        <strong className="quotation-client-company">{(activeLead?.leadCompanyName || "CLIENT COMPANY").toUpperCase()}</strong>
        <p>Thank you for choosing {companyDisplayName}. It was a pleasure speaking with you!</p>
        <p>In reference to the specific requirements we discussed during our conversation, we are pleased to provide you with the cost details of our services.</p>
      </section>

      <section className="quotation-service-card">
        <div className="quotation-service-header">
          <span>ITEM DESCRIPTION</span>
          <span>QTY</span>
          <span>PRICE</span>
          <span>AMOUNT</span>
        </div>
        <div className="quotation-service-body">
          {invoiceItems.map((item, i) => (
            <div key={item.id || item.name || `service-row-${i}`} className="quotation-service-row">
              <div className="quotation-item-main">
                <span className="quotation-item-name">{item.name}</span>
                {item.description && <span className="quotation-item-desc">{item.description}</span>}
              </div>
              <span style={{ textAlign: "center" }}>{item.quantity}</span>
              <span style={{ textAlign: "right" }}>₹{(Number(item.price) || 0).toLocaleString()}</span>
              <span style={{ textAlign: "right", fontWeight: 700, color: "#0f172a" }}>₹{((Number(item.price) || 0) * (Number(item.quantity) || 1)).toLocaleString()}</span>
            </div>
          ))}

          <div className="quotation-table-bottom-bar">
            <p className="gst-exempt-footnote">{getGstFootnoteText()}</p>
          </div>
        </div>

        {kindNote?.trim() ? (
          <div className="quotation-kind-note-box">
            <p className="quotation-kind-note-text">{kindNote}</p>
          </div>
        ) : null}
      </section>
    </section>
  );
}

function QuotationPreviewPage2({
  dbBannerText,
  bannerTextColor,
  bannerBgColor,
  bannerBgImage,
  quotationNote,
  companyDisplayName,
  companyBankDetails,
  quotationTerms,
  dbContactDetails,
  resolvedFooterBannerUrl,
  setFooterBannerLoadError,
}) {
  const panelStyle = {
    backgroundColor: bannerBgColor || "#785b18",
    ...(bannerBgImage
      ? {
          backgroundImage: `url(${bannerBgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "right center",
          backgroundRepeat: "no-repeat",
        }
      : {}),
  };

  return (
    <section className="quotation-page quotation-terms-page">
      <div className="quotation-partner-panel" style={panelStyle}>
        <div className="quotation-partner-panel-title" style={{ color: bannerTextColor || "#ffffff" }}>{dbBannerText || "Think Software,\nThink Softrate."}</div>
      </div>

      <div className="quotation-terms-grid">
        <div className="quotation-card-box quotation-payable-card">
          <div className="quotation-section-title">Payable To</div>
          <div className="quotation-company-name">{companyDisplayName}</div>

          <div className="quotation-section-title" style={{ marginTop: 12 }}>
            Bank Details
          </div>
          <div className="quotation-bank-list">
            {companyBankDetails.map((row, rIdx) => (
              <div key={row.label || `bank-row-${rIdx}`} className="quotation-bank-row">
                <span className="quotation-bank-label">{row.label}</span>
                <span className="quotation-bank-colon">:</span>
                <span className="quotation-bank-val">{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="quotation-card-box quotation-conditions-card">
          <div className="quotation-section-title">Terms and conditions:</div>
          <ul className="quotation-conditions-list">
            {quotationTerms.map((term, tIdx) => (
              <li key={typeof term === "string" ? term : `term-${tIdx}`}>{term}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="quotation-card-box quotation-mission-card">
        <p className="quotation-mission-text" style={{ whiteSpace: "pre-line", margin: 0 }}>
          {quotationNote || "We aim to provide the best software to automate your business (Our charges are 50% less than market price with a mantra “High quality at affordable cost”).\nThe price mentioned above is exclusive of applicable tax."}
        </p>
      </div>

      <footer className="quotation-terms-footer">
        <strong className="quotation-footer-company">{companyDisplayName}</strong>
        <span className="quotation-footer-contact">
          {dbContactDetails?.email || "info@softrate.org"} | {dbContactDetails?.phone || "+91 90000 00000"}
        </span>
        <span className="quotation-footer-copy">Copyright © {new Date().getFullYear()} All Rights Reserved</span>

        {resolvedFooterBannerUrl && (
          <div className="quotation-certifications-wrap" style={{ marginTop: 10, textAlign: "center", width: "100%" }}>
            <img src={resolvedFooterBannerUrl} alt="Certifications & Accreditations" onError={() => setFooterBannerLoadError(true)} style={{ maxWidth: "100%", maxHeight: 58, objectFit: "contain", display: "inline-block" }} />
          </div>
        )}
      </footer>
    </section>
  );
}

function QuotationBottomActions({ onClose, handlePrint, isSaving, isViewingSaved, invoiceItemsCount, activeMobileView, setActiveMobileView }) {
  return (
    <div className="quotation-modal-actions">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Button onClick={onClose}>Cancel</Button>
        {setActiveMobileView && (
          <Button
            className="quotation-bottom-preview-toggle-btn"
            icon={activeMobileView === "preview" ? <EditOutlined /> : <EyeOutlined />}
            onClick={() => setActiveMobileView(activeMobileView === "preview" ? "editor" : "preview")}
          >
            {activeMobileView === "preview" ? "Edit Form" : "Preview"}
          </Button>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Button icon={<MailOutlined />} onClick={() => handlePrint("email")} loading={isSaving}>
          Send Mail
        </Button>
        <Button icon={<PrinterOutlined />} onClick={() => handlePrint("print")} loading={isSaving} disabled={invoiceItemsCount === 0}>
          {isViewingSaved ? "Print Quote" : "Save & Print"}
        </Button>
        <Button type="primary" icon={<CheckOutlined />} onClick={() => handlePrint("save")} loading={isSaving} disabled={invoiceItemsCount === 0}>
          {isViewingSaved ? "Update" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function QuotationModal({ visible, open, onClose, onSaveQuotation, onQuotationSent, lead, prefillLead, record, savedQuotationRecord, companyCode: propCompanyCode, employeeId: propEmployeeId, autoOpenMail = false }) {
  const isModalOpen = Boolean(visible || open);
  const activeLead = lead || prefillLead;
  const activeRecord = record || savedQuotationRecord;
  const isViewingSaved = Boolean(activeRecord);

  const { companyCode: sessionCompanyCode, user } = useAuthSession();
  const companyInfo = useSelector((state) => state.executor?.companyInfo);

  const activeCompanyCode = propCompanyCode || companyInfo?.companyCode || sessionCompanyCode || "";
  const activeEmployeeId = propEmployeeId || user?.id || "";
  const activeEmployeeName = user?.name || "";
  const activeRole = user?.role || "employee";
  const allowedCompanies = user?.allowedCompanies || [];

  // Unified Database-fetched settings
  const [dbSettings, setDbSettings] = useState(DEFAULT_DB_SETTINGS);
  const [logoLoadError, setLogoLoadError] = useState(false);
  const [heroBannerLoadError, setHeroBannerLoadError] = useState(false);
  const [footerBannerLoadError, setFooterBannerLoadError] = useState(false);

  // Form States
  const [documentCompanyCode, setDocumentCompanyCode] = useState(activeCompanyCode);
  const [invoiceIssuedAt, setInvoiceIssuedAt] = useState(dayjs().format("YYYY-MM-DD"));
  const [quoteNumberStr, setQuoteNumberStr] = useState("");
  const [selectedInvoiceProduct, setSelectedInvoiceProduct] = useState(null);
  const [invoicePrice, setInvoicePrice] = useState(0);
  const [invoiceQuantity, setInvoiceQuantity] = useState(1);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [documentGstPercentageOverride, setDocumentGstPercentageOverride] = useState(null);
  const [quotationKindNoteDraft, setQuotationKindNoteDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [mailDrawerOpen, setMailDrawerOpen] = useState(false);
  const [mailPrefillData, setMailPrefillData] = useState(null);
  const [activeMobileView, setActiveMobileView] = useState("editor");

  // Client Company Deals in Pipeline
  const [companyDeals, setCompanyDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [bundledDealIds, setBundledDealIds] = useState([]);

  const initialOpenRef = useRef(false);

  // Load all deals associated with this company
  const loadCompanyDeals = useCallback(async () => {
    if (!isModalOpen || !activeLead) return;
    const compName = activeLead.leadCompanyName || activeLead.companyName || "";
    const phone = activeLead.contactNumber || "";
    const lId = activeLead.leadId || activeLead._id || "";
    if (!compName && !phone && !lId) return;

    setLoadingDeals(true);
    try {
      const res = await getDeals(documentCompanyCode || activeCompanyCode, {
        leadCompanyName: compName,
        contactNumber: phone,
        leadId: lId,
        excludeClosed: true,
      });
      let dealsList = [];
      if (Array.isArray(res?.data)) {
        dealsList = res.data;
      } else if (Array.isArray(res?.deals)) {
        dealsList = res.deals;
      }
      setCompanyDeals(dealsList);
    } catch (err) {
      console.error("[QuotationModal] loadCompanyDeals error:", err);
    } finally {
      setLoadingDeals(false);
    }
  }, [isModalOpen, activeLead, documentCompanyCode, activeCompanyCode]);

  useEffect(() => {
    if (isModalOpen) {
      loadCompanyDeals();
    }
  }, [isModalOpen, loadCompanyDeals]);

  // Fetch DB Settings for company
  const loadDbSettings = useCallback(
    async (compCode) => {
      if (!compCode) return;
      try {
        const res = await getCompanySettings(compCode);
        if (res?.success && res.settings) {
          const parsed = parseCompanySettings(res.settings);
          setDbSettings(parsed);
          setLogoLoadError(false);
          setHeroBannerLoadError(false);
          setFooterBannerLoadError(false);
          if (parsed.invoiceFooter && !activeRecord) {
            setQuotationKindNoteDraft(parsed.invoiceFooter);
          }
        }
      } catch (err) {
        console.error("[QuotationModal] loadDbSettings error:", err);
      }
    },
    [activeRecord],
  );

  useEffect(() => {
    if (isModalOpen) {
      loadDbSettings(documentCompanyCode);
    }
  }, [isModalOpen, documentCompanyCode, loadDbSettings]);

  const visibleProducts = useMemo(() => dbSettings.products, [dbSettings.products]);
  const quotationTerms = useMemo(() => dbSettings.terms, [dbSettings.terms]);

  const resolvedHeroBannerUrl = useMemo(() => {
    if (heroBannerLoadError || !dbSettings.heroBanner) return "";
    return resolveLogoUrl(dbSettings.heroBanner);
  }, [dbSettings.heroBanner, heroBannerLoadError]);

  const resolvedFooterBannerUrl = useMemo(() => {
    if (footerBannerLoadError || !dbSettings.footerBanner) return "";
    return resolveLogoUrl(dbSettings.footerBanner);
  }, [dbSettings.footerBanner, footerBannerLoadError]);

  // Initialize Modal Data
  useEffect(() => {
    if (isModalOpen && !initialOpenRef.current) {
      initialOpenRef.current = true;
      const initial = getInitialQuotationFormState({
        activeRecord,
        activeLead,
        activeCompanyCode,
        visibleProducts,
      });

      setQuoteNumberStr(initial.quoteNumberStr);
      setInvoiceIssuedAt(initial.invoiceIssuedAt);
      setInvoiceItems(initial.invoiceItems);
      setQuotationKindNoteDraft(initial.quotationKindNoteDraft);
      setDocumentCompanyCode(initial.documentCompanyCode);
      setSelectedInvoiceProduct(initial.selectedInvoiceProduct);
      setInvoicePrice(initial.invoicePrice);
      setInvoiceQuantity(initial.invoiceQuantity);
      setEditingItemIndex(null);
      setDocumentGstPercentageOverride(initial.documentGstPercentageOverride);
      setBundledDealIds(activeLead?.dealId ? [activeLead.dealId] : []);
    } else if (!isModalOpen) {
      initialOpenRef.current = false;
      setEditingItemIndex(null);
      setBundledDealIds([]);
      setCompanyDeals([]);
    }
  }, [isModalOpen, activeRecord, activeLead, activeCompanyCode, visibleProducts]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add("modal-open");
      return () => {
        document.body.classList.remove("modal-open");
      };
    }
  }, [isModalOpen]);

  const handleDateChange = (newDate) => {
    setInvoiceIssuedAt(newDate);
    if (!activeRecord) {
      setQuoteNumberStr(getDraftQuotationNumber(newDate));
    }
  };

  const handleProductSelect = (value, selectedOptions) => {
    if (!value || value.length === 0) {
      setSelectedInvoiceProduct(null);
      setInvoicePrice(0);
      return;
    }
    const resolved = resolveCascaderProduct(buildProductCascaderOptions(visibleProducts), value, selectedOptions);
    if (resolved) {
      setSelectedInvoiceProduct({
        name: resolved.displayName || resolved.name,
        price: resolved.price || 0,
        description: resolved.description || "",
        sacHsn: resolved.sacHsn || "",
      });
      setInvoicePrice(resolved.price || 0);
    }
  };

  const handleEditInvoiceItem = (index) => {
    const item = invoiceItems[index];
    if (!item) return;
    setEditingItemIndex(index);
    setSelectedInvoiceProduct({
      name: item.name,
      price: item.price || 0,
      description: item.description || "",
      sacHsn: item.sacHsn || "",
    });
    setInvoicePrice(item.price || 0);
    setInvoiceQuantity(item.quantity || 1);
  };

  const handleCancelEditInvoiceItem = () => {
    setEditingItemIndex(null);
    setSelectedInvoiceProduct(null);
    setInvoicePrice(0);
    setInvoiceQuantity(1);
  };

  const handleAddInvoiceItem = () => {
    if (!selectedInvoiceProduct) return;
    const newItem = {
      name: selectedInvoiceProduct.name,
      quantity: Number(invoiceQuantity) || 1,
      price: Number(invoicePrice) || 0,
      description: selectedInvoiceProduct.description || "",
      sacHsn: selectedInvoiceProduct.sacHsn || "",
    };

    if (editingItemIndex !== null && editingItemIndex >= 0 && editingItemIndex < invoiceItems.length) {
      const updated = [...invoiceItems];
      updated[editingItemIndex] = newItem;
      setInvoiceItems(updated);
      setEditingItemIndex(null);
      message.success(`Updated ${newItem.name}`);
    } else {
      setInvoiceItems((prev) => [...prev, newItem]);
      message.success(`Added ${newItem.name}`);
    }

    setSelectedInvoiceProduct(null);
    setInvoicePrice(0);
    setInvoiceQuantity(1);
  };

  const handleRemoveInvoiceItem = (index) => {
    if (invoiceItems.length <= 1) {
      message.warning("Quotation must contain at least one item.");
      return;
    }
    if (editingItemIndex === index) {
      handleCancelEditInvoiceItem();
    } else if (editingItemIndex !== null && index < editingItemIndex) {
      setEditingItemIndex(editingItemIndex - 1);
    }
    setInvoiceItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddDealItem = useCallback((deal) => {
    if (!deal) return;
    const dealPrice = Number(deal.amount) || 0;
    const dealName = deal.dealName || "Service Item";
    const newItem = {
      name: dealName,
      price: dealPrice,
      quantity: 1,
      description: deal.description || "",
      dealId: deal._id,
    };

    setInvoiceItems((prev) => {
      if (prev.some((it) => String(it.name || "").trim().toLowerCase() === dealName.trim().toLowerCase())) {
        return prev;
      }
      return [...prev, newItem];
    });

    if (deal._id) {
      setBundledDealIds((prev) => (prev.includes(deal._id) ? prev : [...prev, deal._id]));
    }
    message.success(`Added "${dealName}" to quotation!`);
  }, []);

  const handleAddAllDeals = useCallback(() => {
    let count = 0;
    const addedIds = [];
    setInvoiceItems((prev) => {
      const next = [...prev];
      companyDeals.forEach((deal) => {
        const dealName = deal.dealName || "Service Item";
        const exists = next.some((it) => String(it.name || "").trim().toLowerCase() === dealName.trim().toLowerCase());
        if (!exists) {
          next.push({
            name: dealName,
            price: Number(deal.amount) || 0,
            quantity: 1,
            description: deal.description || "",
            dealId: deal._id,
          });
          if (deal._id) addedIds.push(deal._id);
          count++;
        }
      });
      return next;
    });

    if (addedIds.length > 0) {
      setBundledDealIds((prev) => Array.from(new Set([...prev, ...addedIds])));
    }
    if (count > 0) {
      message.success(`Added ${count} deals to quotation!`);
    }
  }, [companyDeals]);

  const clientGstNumber = activeLead?.gstNumber || activeLead?.gstin || activeRecord?.gstNumber || activeRecord?.clientSnapshot?.gstNumber || "";
  const hasCustomerGst = Boolean(clientGstNumber && String(clientGstNumber).trim() && String(clientGstNumber).trim() !== "—");

  const effectiveGstRate = useMemo(() => {
    if (documentGstPercentageOverride === -1) return 0;
    if (documentGstPercentageOverride !== null) return Number(documentGstPercentageOverride);
    return hasCustomerGst ? Number(dbSettings.gstPercentage) : 0;
  }, [documentGstPercentageOverride, hasCustomerGst, dbSettings.gstPercentage]);

  const invoiceSubtotal = useMemo(() => {
    return invoiceItems.reduce((acc, item) => acc + (Number(item.price) || 0) * (Number(item.quantity) || 1), 0);
  }, [invoiceItems]);

  const gstAmount = useMemo(() => {
    return (invoiceSubtotal * effectiveGstRate) / 100;
  }, [invoiceSubtotal, effectiveGstRate]);

  const grandTotal = useMemo(() => {
    return invoiceSubtotal + gstAmount;
  }, [invoiceSubtotal, gstAmount]);

  const companyDisplayName = useMemo(() => {
    return dbSettings.companyName || companyInfo?.companyName || user?.companyName || "SOFTRATE TECHNOLOGY PVT LTD";
  }, [dbSettings.companyName, companyInfo, user]);

  const companyBankDetails = useMemo(() => {
    return formatBankDetails(dbSettings.bankDetails, dbSettings.bankDetails2, companyInfo?.bankDetails, hasCustomerGst);
  }, [hasCustomerGst, dbSettings.bankDetails, dbSettings.bankDetails2, companyInfo]);

  const prepareAndOpenMailComposer = async (finalDocNumber) => {
    const docNumber = finalDocNumber || quoteNumberStr || "QT26001";
    const mailData = await generateQuotationEmailData({
      activeLead,
      docNumber,
      invoiceIssuedAt,
      grandTotal,
      activeEmployeeName,
      companyDisplayName,
    });
    setMailPrefillData(mailData);
    setMailDrawerOpen(true);
  };

  const handlePrint = async (actionType = "print") => {
    if (invoiceItems.length === 0) {
      message.error("Please add at least one line item.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        quotationNumber: quoteNumberStr,
        companyCode: documentCompanyCode,
        leadId: activeLead?.id || activeLead?._id || activeRecord?.leadId,
        leadCompanyName: activeLead?.leadCompanyName || activeRecord?.leadCompanyName || "Client Company",
        contactName: activeLead?.contactName || activeRecord?.contactName || "Sir/Ma'am",
        contactNumber: activeLead?.contactNumber || activeRecord?.contactNumber || "",
        directorEmailAddress: activeLead?.directorEmailAddress || activeRecord?.directorEmailAddress || "",
        gstNumber: clientGstNumber,
        quotationDate: invoiceIssuedAt,
        employeeId: activeEmployeeId,
        employeeName: activeEmployeeName,
        createdById: activeEmployeeId,
        createdByName: activeEmployeeName,
        createdByRole: activeRole,
        dealIds: bundledDealIds,
        items: invoiceItems.map((item) => ({
          name: item.name,
          quantity: Number(item.quantity),
          rate: Number(item.price),
          description: item.description || "",
        })),
        gstRate: effectiveGstRate,
        gstAmount: gstAmount,
        kindNote: quotationKindNoteDraft,
        subtotal: invoiceSubtotal,
        total: grandTotal,
      };

      let res;
      let savedNumber = quoteNumberStr;
      if (activeRecord?._id) {
        res = await updateQuotation(documentCompanyCode, activeRecord._id, payload);
        savedNumber = res?.quotation?.quotationNumber || quoteNumberStr;
        message.success(`Quotation ${savedNumber} updated successfully!`);
      } else {
        res = await createQuotation(documentCompanyCode, payload);
        savedNumber = res?.quotation?.quotationNumber || quoteNumberStr;
        setQuoteNumberStr(savedNumber);
        message.success(`Quotation ${savedNumber} saved successfully!`);
      }

      if (onSaveQuotation) onSaveQuotation({ ...payload, quotationNumber: savedNumber, employeeName: activeEmployeeName }, bundledDealIds);

      if (actionType === "email") {
        await prepareAndOpenMailComposer(savedNumber);
      } else if (actionType === "print") {
        const previewEl = document.querySelector(".quotation-builder-preview");
        if (previewEl) {
          const styles = Array.from(document.querySelectorAll("style, link[rel='stylesheet']"))
            .map((s) => s.outerHTML)
            .join("\n");
          const printFrame = document.createElement("iframe");
          printFrame.style.position = "fixed";
          printFrame.style.right = "0";
          printFrame.style.bottom = "0";
          printFrame.style.width = "0";
          printFrame.style.height = "0";
          printFrame.style.border = "none";
          printFrame.style.zIndex = "-1";
          document.body.appendChild(printFrame);

          const frameDoc = printFrame.contentWindow.document;
          frameDoc.open();
          frameDoc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Quotation - ${savedNumber || "QUOTE"}</title>
  ${styles}
  <style>
    @page { size: A4 portrait; margin: 0; }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  </style>
</head>
<body>
  ${previewEl.outerHTML}
</body>
</html>`);
          frameDoc.close();

          setTimeout(() => {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
            setTimeout(() => {
              try {
                if (document.body.contains(printFrame)) {
                  document.body.removeChild(printFrame);
                }
              } catch (e) {
                // ignore
              }
            }, 3000);
          }, 250);
        } else {
          window.print();
        }
      }
    } catch (err) {
      console.error("Save quotation error:", err);
      message.error("Failed to save quotation.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isModalOpen) return null;

  return (
    <div className="quotation-modal-overlay">
      <div className="quotation-modal-container">
        <QuotationHeader isViewingSaved={isViewingSaved} quoteNumberStr={quoteNumberStr} allowedCompanies={allowedCompanies} documentCompanyCode={documentCompanyCode} setDocumentCompanyCode={setDocumentCompanyCode} activeCompanyCode={activeCompanyCode} invoiceIssuedAt={invoiceIssuedAt} handleDateChange={handleDateChange} onClose={onClose} />

        <div className="quotation-builder-layout">
          <div className="quotation-builder-controls">
            {isViewingSaved && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "7px 12px",
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  fontSize: "0.78rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "#64748b" }}>Editing</span>
                  <span style={{ fontWeight: 600, color: "#0f172a" }}>{quoteNumberStr}</span>
                </div>
                <span
                  style={{
                    fontSize: "0.68rem",
                    color: "#64748b",
                    background: "#f1f5f9",
                    padding: "2px 8px",
                    borderRadius: 12,
                    fontWeight: 500,
                  }}
                >
                  Saved Quote
                </span>
              </div>
            )}

            <QuotationCompanyDealsSelector
              companyDeals={companyDeals}
              loadingDeals={loadingDeals}
              invoiceItems={invoiceItems}
              onAddDealItem={handleAddDealItem}
              onAddAllDeals={handleAddAllDeals}
              companyName={activeLead?.leadCompanyName || activeLead?.companyName}
            />

            <QuotationProductControls
              visibleProducts={visibleProducts}
              selectedInvoiceProduct={selectedInvoiceProduct}
              handleProductSelect={handleProductSelect}
              invoicePrice={invoicePrice}
              setInvoicePrice={setInvoicePrice}
              invoiceQuantity={invoiceQuantity}
              setInvoiceQuantity={setInvoiceQuantity}
              handleAddInvoiceItem={handleAddInvoiceItem}
              editingItemIndex={editingItemIndex}
              handleCancelEditInvoiceItem={handleCancelEditInvoiceItem}
            />

            <QuotationItemsTable
              invoiceItems={invoiceItems}
              handleEditInvoiceItem={handleEditInvoiceItem}
              handleRemoveInvoiceItem={handleRemoveInvoiceItem}
              editingItemIndex={editingItemIndex}
            />

            <QuotationGstRateSelector effectiveGstRate={effectiveGstRate} hasCustomerGst={hasCustomerGst} dbGstPercentage={dbSettings.gstPercentage} documentGstPercentageOverride={documentGstPercentageOverride} setDocumentGstPercentageOverride={setDocumentGstPercentageOverride} />

            <label className="quotation-control-card quotation-mini-field" style={{ gap: 8 }}>
              <span>Kind Note</span>
              <textarea className="quotation-field-textarea" rows={3} value={quotationKindNoteDraft} onChange={(e) => setQuotationKindNoteDraft(e.target.value)} placeholder="Add the kind note for this quotation..." />
            </label>

            <Button
              className="quotation-view-preview-btn-mobile"
              icon={<EyeOutlined />}
              onClick={() => setActiveMobileView("preview")}
              style={{ width: "100%", height: 38, borderRadius: 8, fontWeight: 500, marginTop: 4 }}
            >
              View Document Preview
            </Button>
          </div>

          <div className="quotation-builder-preview" id="quotation-print-area">
            <QuotationPreviewPage1
              resolvedHeroBannerUrl={resolvedHeroBannerUrl}
              dbCompanyLogo={dbSettings.companyLogo}
              logoLoadError={logoLoadError}
              setLogoLoadError={setLogoLoadError}
              companyDisplayName={companyDisplayName}
              quoteNumberStr={quoteNumberStr}
              invoiceIssuedAt={invoiceIssuedAt}
              activeLead={activeLead}
              invoiceItems={invoiceItems}
              effectiveGstRate={effectiveGstRate}
              documentGstPercentageOverride={documentGstPercentageOverride}
              hasCustomerGst={hasCustomerGst}
              kindNote={quotationKindNoteDraft}
            />

            <QuotationPreviewPage2
              dbBannerText={dbSettings.bannerText}
              bannerTextColor={dbSettings.bannerTextColor}
              bannerBgColor={dbSettings.bannerBgColor}
              bannerBgImage={dbSettings.bannerBgImage}
              quotationNote={dbSettings.quotationNote}
              companyDisplayName={companyDisplayName}
              companyBankDetails={companyBankDetails}
              quotationTerms={quotationTerms}
              dbContactDetails={dbSettings.contactDetails}
              resolvedFooterBannerUrl={resolvedFooterBannerUrl}
              setFooterBannerLoadError={setFooterBannerLoadError}
            />
          </div>
        </div>

        <QuotationBottomActions
          onClose={onClose}
          handlePrint={handlePrint}
          isSaving={isSaving}
          isViewingSaved={isViewingSaved}
          invoiceItemsCount={invoiceItems.length}
          activeMobileView={activeMobileView}
          setActiveMobileView={setActiveMobileView}
        />

        <MailComposeDrawer
          open={mailDrawerOpen}
          visible={mailDrawerOpen}
          onClose={() => {
            setMailDrawerOpen(false);
            setMailPrefillData(null);
          }}
          lead={activeLead}
          prefillData={mailPrefillData}
          companyCode={documentCompanyCode || activeCompanyCode}
          employeeInfo={{ id: activeEmployeeId, name: activeEmployeeName, companyName: companyDisplayName }}
          onSuccess={() => {
            message.success("Quotation sent via email!");
            if (onQuotationSent) onQuotationSent();
          }}
        />
      </div>
    </div>
  );
}

export default QuotationModal;
